import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import * as IoIosIcons from 'react-icons/io';
import { useLoginStore, useFeedStore } from '@lib/store';
import { useMediaQuery, useRouteHook } from '@shared/hooks';
import { LocalStorageUtils, RoutePaths } from '@shared/utils';
import { ErrorLoginModal } from './widgets';
import { Footer } from '@widgets';
import { Button, Input, Radio } from '@shared/widgets';
import logoImg from '@assets/images/logo.png';

interface IFormData {
  userName: string;
  password: string;
  rememberMe?: boolean;
}

export const LoginPage = () => {
  const { navigate } = useRouteHook();

  const loginStore = useLoginStore();
  const feedStore = useFeedStore();

  const [errorModal, setErrorModal] = useState<boolean>(false);
  const [errorState, setErrorState] = useState(false);

  const formControl = useForm<IFormData>({ defaultValues: { userName: '', password: '', rememberMe: false } });

  const isMaxMd = useMediaQuery('(max-width: 768px)');

  const onSubmit = async (data?: IFormData) => {
    if (!data) return;
    await loginStore.login({ userName: data.userName, password: data.password });
    if (data.rememberMe) LocalStorageUtils.setRememberMe({ userName: data?.userName, password: data?.password });
  };

  useEffect(() => {
    const credentials = LocalStorageUtils.getRememberMe();
    if (credentials) {
      formControl.setValue('userName', credentials.userName);
      formControl.setValue('password', credentials.password);
    }

    feedStore.getFeed(1);
  }, []);

  useEffect(() => {
    const handleEnterPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter') onSubmit();
    };

    document.addEventListener('keypress', handleEnterPress);

    LocalStorageUtils.removeStuff();

    return () => {
      document.removeEventListener('keypress', handleEnterPress);
    };
  }, [onSubmit]);

  useEffect(() => {
    if (loginStore.success) {
      if (loginStore.data?.token && !loginStore.data?.secondFactorEnabled && loginStore.data?.refreshToken) {
        LocalStorageUtils.setStuff({ ...loginStore.data });
        !isMaxMd ? navigate(RoutePaths.home.onNavigate()) : navigate(RoutePaths.cards.onNavigate());
        setErrorModal(false);
      } else if (loginStore.data?.secondFactorEnabled) {
        LocalStorageUtils.setStuff({ ...loginStore.data! });
        navigate(RoutePaths.loginCode.onNavigate());
        setErrorModal(false);
      } else {
        setErrorState(true);
      }
    }
  }, [loginStore]);

  const currentNews = feedStore.data.length !== 0 ? feedStore.data[0] : false;

  return (
    <>
      <div className='absolute top-[10px] left-[67px] z-[16] h-[50px] w-[50px] max-md:top-0 max-md:left-0 max-md:z-[10] max-md:w-full max-md:bg-[#0f0f114d]'>
        <NavLink className='mx-auto flex h-[50px] w-[50px] items-center justify-center max-md:h-[44px]' to={RoutePaths.home.onNavigate()}>
          <img className='h-full w-full object-contain' src={logoImg} alt='logo' />
        </NavLink>
      </div>
      <div className='mx-auto flex h-[702px] w-[1440px] overflow-hidden max-md:relative max-md:z-1 max-md:h-auto max-md:w-full max-md:flex-col-reverse'>
        <div className='relative z-15 w-[40%] after:absolute after:top-[-30%] after:left-[-50%] after:z-[20] after:h-[1000px] after:w-[1000px] after:rotate-[17deg] after:bg-[#fff] max-md:w-full max-md:after:top-[-25px] max-md:after:h-[200px] max-md:after:w-[200vw] max-md:after:rotate-[-7deg]'>
          <div className='relative z-99 w-[140px] max-md:hidden' />
          <div className='relative z-99 h-[702px] w-full bg-right bg-no-repeat pl-[150px] text-left max-md:h-auto max-md:pt-0 max-md:pl-0'>
            <form onSubmit={formControl.handleSubmit(onSubmit)}>
              <div className='w-[590px] max-md:relative max-md:w-full max-md:px-4 max-md:pt-5 max-md:pb-12'>
                <h2 className='mt-[103px] font-[Prata] text-[56px] text-[#272727] max-md:mt-0 max-md:text-center max-md:text-[28px]'>
                  Online Network
                </h2>
                <p className='mt-2 mb-7.5 text-lg font-light text-[#4c4c4c] before:absolute before:left-0 before:mt-[15px] before:h-[1px] before:w-[125px] before:bg-[#000] max-md:text-center max-md:text-[#4c4c4c80] max-md:before:hidden'>
                  Enter the world of Insignia
                </p>
                <div className='mt-2 flex items-center gap-2 max-md:mt-4 max-md:block'>
                  <Controller
                    name='userName'
                    control={formControl.control}
                    rules={{ maxLength: 100 }}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                      <>
                        <Input inputClassName='w-[292px]' required label='Username' name='userName' value={value} onChange={onChange} />{' '}
                        {(loginStore.error || errorState || error) && (
                          <div className='mt-8 flex items-baseline gap-1 max-md:hidden'>
                            <div className='h-[13px] w-[12px] rounded-full bg-[#d0021b] text-center text-xs font-bold text-[#fff]'>!</div>
                            <div className='text-xs font-bold text-[#d0021b] uppercase'>INCORRECT USERNAME</div>
                          </div>
                        )}
                      </>
                    )}
                  />
                </div>
                <div className='mt-2 flex items-center gap-2 max-md:mt-4 max-md:block'>
                  <Controller
                    name='password'
                    control={formControl.control}
                    rules={{ maxLength: 100 }}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                      <>
                        <Input
                          inputClassName='w-[292px]'
                          required
                          label='Password'
                          name='password'
                          type='password'
                          value={value}
                          onChange={onChange}
                        />
                        {(loginStore.error || errorState || error) && (
                          <div className='mt-8 flex items-baseline gap-1 max-md:hidden'>
                            <div className='h-[13px] w-[12px] rounded-full bg-[#d0021b] text-center text-xs font-bold text-[#fff]'>!</div>
                            <div className='text-xs font-bold text-[#d0021b] uppercase'>INCORRECT PASSWORD</div>
                          </div>
                        )}
                      </>
                    )}
                  />
                </div>
                {(loginStore.error || errorState) && (
                  <div className='max-md:inherit my-6 hidden rounded-lg bg-[#e543381a] p-4'>
                    <div className='mt-4 mb-2 ml-2 flex items-center gap-1 rounded-[10px] bg-[#e543381a] p-4 text-left'>
                      <div className='mr-1 h-[13px] w-[12px] rounded-full bg-[#d0021b] text-center text-xs font-bold text-[#ffffff]'>!</div>
                      <div className='text-xs font-bold text-[#d0021b] uppercase'>INCORRECT USERNAME</div>
                    </div>
                    <div className='mt-4 mb-2 ml-2 flex items-center gap-1 rounded-[10px] bg-[#e543381a] p-4 text-left'>
                      <div className='mr-1 h-[13px] w-[12px] rounded-full bg-[#d0021b] text-center text-xs font-bold text-[#ffffff]'>!</div>
                      <div className='text-xs font-bold text-[#d0021b] uppercase'>INCORRECT PASSWORD</div>
                    </div>
                  </div>
                )}
                <Controller
                  name='rememberMe'
                  control={formControl.control}
                  rules={{ maxLength: 100 }}
                  render={({ field: { value, onChange } }) => (
                    <Radio className='mt-3 max-md:mt-4' checked={value} onClick={onChange}>
                      Remember my username
                    </Radio>
                  )}
                />
                <div className='mt-4'>
                  <span className='cursor-pointer font-medium text-[#000000]' onClick={() => navigate(RoutePaths.recovery.onNavigate())}>
                    Have you forgotten your password?
                  </span>
                </div>
                <div className='mt-4' onClick={() => navigate(RoutePaths.masterKey.onNavigate())}>
                  <span className='cursor-pointer font-medium text-[#000000]'>Download Master Key</span>
                </div>
                <Button
                  className='mt-5 w-[292px] max-md:mt-8 max-md:w-full'
                  isLoading={loginStore.loading}
                  onClick={formControl.handleSubmit(onSubmit)}
                >
                  Login
                </Button>

                {/* Uncomment if needed
              <div className="mt-5 flex items-center max-md:mt-8 max-md:w-full">
                <div className="auth-b1">Do you have an online account?</div>
                <div className="mr-4 flex h-[40px] w-[138px] cursor-pointer items-center justify-center rounded-lg border border-[#000000] font-medium text-[#000000]" onClick={openRegister}>
                  Register
                </div>
              </div>
              */}
              </div>
            </form>
          </div>
        </div>
        <div
          className='relative z-10 w-[60%] bg-cover bg-center bg-no-repeat max-md:w-full max-md:pt-[100%]'
          style={{ background: `url('${currentNews && currentNews?.imageSrc}')` }}
        >
          <div className='flex h-full items-center justify-between pr-[5%] pl-[20%] text-[#fff] max-md:absolute max-md:top-0 max-md:left-0 max-md:w-full max-md:flex-col max-md:items-start max-md:justify-end max-md:px-4 max-md:py-12'>
            <span className='w-1/2 font-[Prata] text-3xl max-md:mb-4 max-md:w-full max-md:text-[28px] max-md:leading-[36px]'>
              {currentNews && currentNews?.title}
            </span>
            {currentNews && currentNews?.imageHref && (
              <a
                href={currentNews.imageHref}
                className='flex flex-col items-center transition-all duration-300 after:mt-1 after:h-[2px] after:w-0 after:bg-[#d7c29a] after:transition-all after:duration-300 hover:after:w-full'
                target='_blank'
                rel='noopener noreferrer'
              >
                <IoIosIcons.IoIosArrowForward className='h-[60px] w-[60px] rounded-full bg-[#d7c29a] p-4 text-3xl text-[#000] max-md:hidden' />
                <p className='mt-1 font-bold text-[#d7c29a]'>Read More</p>
              </a>
            )}
          </div>
        </div>
      </div>
      <Footer />
      <ErrorLoginModal isOpen={errorModal} onClose={() => setErrorModal(false)} />
    </>
  );
};
